import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useAccountIntegrationDonorPerfectApiKey,
	useAccountIntegrationDonorPerfectDeleteApiKey,
} from '../../../../queries';
import donorPerfectLogo from '../../../assets/logo_donorperfect.png';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Input } from '../../../components-v2/Input';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const DonorPerfect = (props: IProps) => {
	const { className, styles } = props;
	const [apiKey, setApiKey] = React.useState<string>('');
	const userSession = useUserSession();

	const donorPerfectIntegration = userSession.account.integrations?.donorPerfect ?? {};

	const saveApiKeyMutation = useAccountIntegrationDonorPerfectApiKey({
		onError: () => {
			toast.error(`An error occurred while saving the API key.  If the problem persists, please contact support.`);
		},
		onSuccess: () => {
			donorPerfectIntegration.isConnected = true;
			userSession.account.setIntegration(Api.ConfigurableIntegrationType.DonorPerfect, donorPerfectIntegration);
			setApiKey('');
		},
	});

	const deleteApiKeyMutation = useAccountIntegrationDonorPerfectDeleteApiKey({
		onError: () => {
			toast.error(`An error occurred while deleting the API key.  If the problem persists, please contact support.`);
		},
		onSuccess: () => {
			donorPerfectIntegration.isConnected = false;
			userSession.account.setIntegration(Api.ConfigurableIntegrationType.DonorPerfect, donorPerfectIntegration);
		},
	});

	const onSaveClicked = () => {
		const value = apiKey?.trim();
		if (!value) {
			return;
		}

		saveApiKeyMutation.mutate({
			apiKey: { apiKey },
		});
	};

	const isConnected = donorPerfectIntegration.isConnected ?? false;
	const onApiKeyValueChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
		setApiKey(ev.target.value);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}>
			<div className={css(integrationsStyleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={donorPerfectLogo} alt='DonorPerfect Logo' />
			</div>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with DonorPerfect to pull donor information, including names, email and physical mailing
				addresses, phone numbers, gifting history, and more.
			</p>
			{!isConnected ? (
				<>
					<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
						To sync Levitate with DonorPerfect, please enter the API Key provided by DonorPerfect Support or their API
						Help Desk.
					</p>
					<div className={css(integrationsStyleSheet.signInContainer, styleSheet.signInContainer)}>
						<div>
							<label htmlFor='apiKey'>API Key</label>
							<Input
								autoComplete='off'
								id='apiKey'
								onChange={onApiKeyValueChanged}
								placeholder='Enter API Key here'
								type='text'
								value={apiKey}
							/>
						</div>
						<div className={css(styleSheet.center)}>
							<button
								className={css(baseStyleSheet.ctaButton, styleSheet.button)}
								onClick={onSaveClicked}
								disabled={saveApiKeyMutation.isPending}
							>
								Save
							</button>
						</div>

						{saveApiKeyMutation.isPending && (
							<LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />
						)}
					</div>
				</>
			) : (
				<>
					<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
						You are successfully connected.
					</p>
					<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
						If you wish to disconnect this integration, click{' '}
						<Link
							to='#'
							className={css(styleSheet.link)}
							onClick={ev => {
								ev.preventDefault();

								deleteApiKeyMutation.mutate();
							}}
						>
							here
						</Link>
						.
					</p>

					{deleteApiKeyMutation.isPending && (
						<LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />
					)}
				</>
			)}
		</div>
	);
};
